// Repair estimates on a defect, a call-out or a customer (Session ARL, gap W-9 as re-scoped by the
// Director on 25 September 2026). The price of a repair, recorded BEFORE the customer has agreed it.
//
// One small panel, mounted on the Defect page (defectId, with the defect's own description as the
// starting wording), on the Call-out page (calloutId) and on the Customer page (customerId). The
// customer page is the estimate's home that outlives its parents: it lists every estimate for the
// customer, whatever it was raised from, so one whose defect or call-out has since been deleted can
// still be seen, answered and deleted, and nothing the customer delete is blocked by is out of reach
// (RULE 1). "Quote this" raises a DRAFT estimate; each row then offers the two ways ELE puts a repair
// price in front of a customer, chosen case by case: a priced letter or a formal repair quotation (both
// drafted in Gmail with the PDF attached). Each makes a DRAFT and nothing more: the office reviews and
// presses Send in Gmail, exactly as for every other customer document (the customer-communication
// auto-send rule). Each send is a branded two-step confirm that names who it is for. There is no Xero
// send (Session ARS, D91): an estimate creates no Xero object; the repair job's completion raises the one
// draft invoice, at the agreed price.
//
// THE ANSWER (Session ARS, D91). The customer approves or declines on the estimate's own page (the link
// rides both emailed documents), or the office records the answer however it reached them (phone, email,
// a signed copy). Each row says which it was. An APPROVAL raises, or attaches to, the repair JOB at the
// agreed price when it is clear which job the work belongs to, and the row says what happened. When it is
// not clear (a different open repair is already on the lift, the estimate has no customer or site),
// nothing is created: the row asks the office, naming the job an attach would use (GET /:id/job-options,
// read live), and the office attaches or raises through a two-step confirm. The same question reaches the
// morning actions ("Approved estimate needs a decision"), derived live.
//
// ONE REPAIR, ONE INVOICE (Session ASC, Director decision D95 (g)). Once the defect's own repair job has been
// SUBMITTED, a new job is never raised for the defect's work. While it waits for approval, the one choice is
// to attach the estimate to THAT job before it is approved (the server offers nothing else). Once it is
// COMPLETE, nothing can be attached or raised: the server's sentence names the job's Xero draft to correct.
// In both states the one exception (Director decision, 25 September 2026) is a separate job raised ONLY by confirming
// the estimate prices new work that job did not do: a secondary control under the refusal (newWorkBlock), offered
// where the server says canRaiseNewWork, its confirm naming the consequence.
//
// While a job is linked, the answer cannot be cleared and the estimate cannot be deleted (the server
// refuses both, 409 job_linked). While that job is still OPEN the office can detach the estimate from it
// (a mistaken attach is undone without cancelling a job that may carry other work) or cancel the job;
// either frees the estimate. While it WAITS FOR APPROVAL it can still be detached (Session ASC), never
// cancelled from here. A complete job can be neither, and the panel never says it can.
//
// ATTACHING FIXED-PRICES THE WHOLE JOB (the review's HIGH finding): the job's one invoice line is this
// estimate's price, parts included, for everything on it. Whenever the target is not exclusively this
// defect's own job, the server's warning sits beside the Attach control and the confirm names the
// consequence. An estimate approved before online answers existed (under ARL, when repairs were raised by
// hand) is cautioned first, so the office checks Scheduling before raising a second job for it.
//
// Every refusal (an edit after a send, deleting an approved estimate) is the server's, shown in its own
// words, and a refusal that says the row has moved on (409) or gone (404) re-reads the list, so the row
// stops offering what was just refused.
import { RecordPicker } from "../../components/RecordPicker";
import { useCallback, useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiError, api } from "../../lib/api";
import type {
  CustomerOption,
  RaiseJobOutcome,
  RepairEstimateDocumentVariant,
  RepairEstimateDto,
  RepairEstimateJobOptions,
  RepairEstimateJobSummary,
  RepairEstimateRaiseJobInput,
  RepairEstimateSendPath,
  RepairEstimateSendResult,
} from "../../lib/api";
import { friendlyApiError } from "../../lib/friendlyApiError";
import { ConfirmButton } from "../../components/ConfirmButton";
import { AccountOnStopNote, AccountSymbol } from "../../components/AccountSymbol";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { StatusPill } from "../../components/StatusPill";
import { Button, buttonClasses } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { Pager } from "../../components/Pager";
import { formatDate, gbpExact, londonDateKey } from "../../lib/format";

// The price bounds the server enforces (MIN_AMOUNT_PENCE / MAX_AMOUNT_PENCE in
// server/src/modules/repairEstimates/repairEstimates.ts): at least a penny, at most GBP 100,000, a
// typo guard rather than a commercial limit. Checked here too so the office is told before a round trip.
const MIN_AMOUNT_PENCE = 1;
const MAX_AMOUNT_PENCE = 10_000_000;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const NOT_A_PRICE = "Enter the price as a number of pounds, for example 450 or 450.00.";

// The office types POUNDS; the API takes PENCE. Exact: at most two decimal places, so "12.34" is 1234
// and never a float's 1233. A leading pound sign is forgiven, and so is a comma, but ONLY as a
// thousands separator in its proper place ("1,250.50"). Stripping every comma wherever it sat read a
// decimal-comma habit or a slip ("450,00", "4,50") as 45000 and 450, a price a hundred times too big
// that one more press would put in front of a customer. Anything that is not a plain number is refused rather than
// guessed at (parseFloat alone would read "12abc" as 12).
export function parsePriceToPence(text: string): { pence: number } | { error: string } {
  const typed = text.trim().replace(/^£\s*/, "");
  if (typed === "") return { error: "Enter the price, ex VAT, for example 450 or 450.00." };
  // A comma is a thousands separator and nothing else, and the first group cannot start with a zero:
  // "450,00", "4,50" and "0,500" are refused rather than read as GBP 45,000.00, 450.00 or 500.00.
  if (typed.includes(",") && !/^[1-9]\d{0,2}(,\d{3})+(\.\d{1,2})?$/.test(typed)) return { error: NOT_A_PRICE };
  const t = typed.replace(/,/g, "");
  if (!/^(\d+(\.\d{1,2})?|\.\d{1,2})$/.test(t)) return { error: NOT_A_PRICE };
  const pence = Math.round(parseFloat(t) * 100);
  if (pence < MIN_AMOUNT_PENCE) return { error: "The price must be more than zero." };
  if (pence > MAX_AMOUNT_PENCE) return { error: `The price must be ${gbpExact(MAX_AMOUNT_PENCE / 100)} or less.` };
  return { pence };
}

// Every date is the shared helper (pinned to Europe/London, the platform's time-zone rendering rule). A
// calendar DAY the server already worked out in London (validUntil, YYYY-MM-DD) is read as UTC midnight,
// which is always the same calendar day in London; today's London day, to compare with it, is londonDateKey.

function penceGbp(pence: number): string {
  return gbpExact(pence / 100);
}

// Session ATF: an estimate's price in words, or that it has none yet (raised on site, awaiting the office's price).
// Every place that states the agreed price (an approval, a job's fixed price) is reachable only once it is priced:
// the server refuses an answer to an unpriced estimate, and a database CHECK backs it.
function priceWords(e: Pick<RepairEstimateDto, "amountPence">): string {
  return e.amountPence == null ? "no price yet" : penceGbp(e.amountPence);
}

// "1.5 hours", "1 hour": the engineer's rough time on site, as they gave it.
function hoursWords(h: number): string {
  return `${Number.isInteger(h) ? h : h.toFixed(2).replace(/0$/, "")} hour${h === 1 ? "" : "s"}`;
}

// The status pill is the registry's repairEstimate domain: the Quote Hub's own reading of the same four
// states, so one repair price and one maintenance quote never disagree in colour (a draft purple,
// awaiting the customer blue, a yes green, a no red).

// Office-facing names for each send path (the server's SEND_PATH_LABEL). Never customer-facing.
// "xero_draft" appears only on an estimate sent that way before Session ARS.
const SEND_PATH_LABEL: Record<RepairEstimateSendPath, string> = {
  xero_draft: "Xero draft invoice",
  priced_document: "Priced letter",
  quotation: "Repair quotation",
};

const INPUT = "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-1.5 text-sm text-ele-strong outline-none focus:border-ele-green";
const SMALL_BTN = "rounded-ele border px-3 py-1 font-menu text-xs tracking-wide transition hover:bg-ele-inset disabled:opacity-50";
const NOTE_BOX = "mt-2 rounded-ele border border-ele-purple-accent bg-ele-bg px-3 py-2 text-xs text-ele-body";

// The server's longest site label (repairEstimate.routes.ts, siteLabel).
const MAX_SITE_LABEL = 500;

interface FormResult {
  description: string;
  amountPence: number;
  recipientName: string; // trimmed, "" when blank
  recipientEmail: string; // trimmed, "" when blank
}

interface FormInitial {
  description: string;
  price: string;
  recipientName: string;
  recipientEmail: string;
}

// The one form, used to raise an estimate and to edit a draft. It validates what it can before a round
// trip and shows the server's own refusal otherwise; the caller closes it on success.
function EstimateForm({
  title,
  initial,
  submitLabel,
  busyLabel,
  recipientHint,
  onSubmit,
  onCancel,
}: {
  title: string;
  initial: FormInitial;
  submitLabel: string;
  busyLabel: string;
  recipientHint: string;
  onSubmit: (v: FormResult) => Promise<void>;
  onCancel: () => void;
}) {
  const [description, setDescription] = useState(initial.description);
  const [price, setPrice] = useState(initial.price);
  const [recipientName, setRecipientName] = useState(initial.recipientName);
  const [recipientEmail, setRecipientEmail] = useState(initial.recipientEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    const works = description.trim();
    if (!works) return setError("Describe the repair works, as the customer will read them.");
    // The server's own limit; a long defect description seeded into the form can exceed it.
    if (works.length > 2000) return setError("Keep the description to 2,000 characters or fewer.");
    const parsed = parsePriceToPence(price);
    if ("error" in parsed) return setError(parsed.error);
    const email = recipientEmail.trim();
    if (email && !EMAIL_RE.test(email)) return setError("That email address does not look right. Check it, or leave it blank.");
    setBusy(true);
    try {
      await onSubmit({ description: works, amountPence: parsed.pence, recipientName: recipientName.trim(), recipientEmail: email });
    } catch (err) {
      setError(friendlyApiError(err, "Could not save the estimate. Please try again."));
      setBusy(false);
    }
  }

  return (
    // noValidate: the checks above are the one set of rules and messages; the browser's own bubble for
    // the email field would otherwise stop the submit before them, in different words.
    <form noValidate onSubmit={(e) => void submit(e)} aria-label={title} className="mt-3 rounded-ele border border-ele-purple-accent bg-ele-bg p-3">
      <h3 className="font-heading text-sm uppercase tracking-wide text-ele-strong">{title}</h3>
      <label className="mt-2 block text-sm">
        <span className="mb-1 block text-ele-body">The repair works, as the customer will read them</span>
        <textarea rows={3} maxLength={2000} value={description} onChange={(e) => setDescription(e.target.value)} className={INPUT} />
      </label>
      <label className="mt-2 block text-sm sm:w-56">
        <span className="mb-1 block text-ele-body">Price in pounds, ex VAT</span>
        <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="450.00" className={INPUT} />
      </label>
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="mb-1 block text-ele-body">Addressed to (optional)</span>
          <input maxLength={200} value={recipientName} onChange={(e) => setRecipientName(e.target.value)} className={INPUT} />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-ele-body">Their email (optional)</span>
          <input type="email" maxLength={254} value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} className={INPUT} />
        </label>
      </div>
      <p className="mt-1 text-xs text-ele-body opacity-70">{recipientHint}</p>
      {error && (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="submit" variant="primary" size="sm" disabled={busy}>
          {busy ? busyLabel : submitLabel}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// What a send did, in the office's words. The draft is where the price now waits; nothing has reached
// the customer until the office sends it from there.
function sendNotice(res: RepairEstimateSendResult): string {
  const to = res.outcome.recipient ?? res.estimate.recipientEmail;
  return to ? `Draft created in Gmail to ${to}. Review and send it from Gmail.` : "Draft created in Gmail. Review and send it from Gmail.";
}

// What a delete does beyond the estimate itself, in the server's terms (DELETE /api/repair-estimates/:id):
// a Xero invoice it raised (only an estimate sent through the retired Xero path before Session ARS has
// one) is deleted from Xero only while it is still a DRAFT (the call-out delete's
// scrub reads its live status first, and never touches an approved one), and an email drafted in Gmail
// is never touched at all, so the office is told to discard it there.
function deleteMessage(e: RepairEstimateDto): string {
  const parts = ["This permanently deletes the estimate."];
  if (e.xeroInvoiceId) {
    parts.push("The draft invoice it raised is deleted from Xero while it is still a draft. Once it has been approved in Xero it is left untouched.");
  }
  if (e.hasGmailDraft) parts.push("The email drafted in Gmail is not touched: discard it in Gmail if it should not be sent.");
  parts.push("This cannot be undone.");
  return parts.join(" ");
}

// A refusal that says THIS ROW is out of date: 409, the estimate has moved on (a colleague sent it or
// answered it), or 404, it has gone. Either way the row on
// screen is wrong and would keep offering the action just refused, so the panel re-reads it.
function isStaleRefusal(err: unknown): err is ApiError {
  return err instanceof ApiError && (err.status === 409 || err.status === 404);
}

// ---- The repair job (Session ARS, D91) -------------------------------------------------------------

// A server sentence, or nothing: a bare machine code never reaches the office (friendlyApiError's rule).
function sentenceOrNothing(message: string | null | undefined): string {
  const m = (message ?? "").trim();
  return m.includes(" ") ? m : "";
}

// Where a job is, for a label: the lift's ELE number, or a lift-less job's site.
function jobWhere(job: Pick<RepairEstimateJobSummary, "eleNumber" | "siteLabel">): string | null {
  return job.eleNumber?.trim() || job.siteLabel?.trim() || null;
}

// A job's description for one line of text, cut short. Attaching an estimate appends its own line to the
// job's description on the SAME run of text ("<the job's own words>. Repair estimate ELE-RE-...: <works>",
// repairEstimateJob.ts), so an attached job's description is long and is cut here. Only the first line is
// taken because a description typed by hand may run over several lines.
function jobName(job: Pick<RepairEstimateJobSummary, "description" | "eleNumber" | "siteLabel">, max = 120): string {
  const first = (job.description ?? "").split(/\r?\n/).find((l) => l.trim() !== "")?.trim() ?? "";
  if (first) return first.length > max ? `${first.slice(0, max - 3).trimEnd()}...` : first;
  const where = jobWhere(job);
  return where ? `the repair on ${where}` : "the repair job";
}

// Where the job stands, in plain words. A scheduled job says when; one still waiting to be booked says so.
function jobWhen(job: Pick<RepairEstimateJobSummary, "status" | "scheduledFor">): string {
  switch (job.status) {
    case "scheduled":
      return job.scheduledFor ? `scheduled for ${formatDate(job.scheduledFor)}` : "not yet scheduled";
    case "in_progress":
      return "in progress";
    case "parts_pending":
      return "on hold";
    case "pending_approval":
      return "finished by the engineer, with the job sheet waiting for your approval";
    case "complete":
      return "finished and approved";
    case "cancelled":
      return "cancelled";
    default:
      return job.status.replace(/_/g, " ");
  }
}

// A job that can still be detached from or cancelled: the server's open set (REASSIGNABLE). A job WAITING FOR
// APPROVAL can still be detached from (Session ASC: the server's isDetachableJobStatus) but not cancelled from
// here. A COMPLETE job can be neither, so the office is never told to do either to it.
const OPEN_JOB_STATUSES: ReadonlySet<string> = new Set(["scheduled", "in_progress", "parts_pending"]);
const WAITING_JOB_STATUS = "pending_approval";
const COMPLETE_JOB_STATUS = "complete";
// The server's decision codes when the defect's own repair job has been submitted (repairEstimates.ts): the
// automatic raise's, and the explicit refusal the job-options carry once that job is complete.
const OWN_JOB_COMPLETE_CODES: ReadonlySet<string> = new Set(["remedial_job_complete", "own_remedial_complete"]);

// The job's entry on the Scheduling page, which opens it (`?job=`), on its own day when it has one (the
// page reads `?date=` as a UTC calendar day, the same day the stored instant falls on there).
function schedulingHref(job: Pick<RepairEstimateJobSummary, "id" | "scheduledFor">): string {
  const day = job.scheduledFor && /^\d{4}-\d{2}-\d{2}/.test(job.scheduledFor) ? job.scheduledFor.slice(0, 10) : null;
  return `/office/scheduling?job=${encodeURIComponent(job.id)}${day ? `&date=${day}` : ""}`;
}

// What an attach or raise did, in the office's words. `afterApproval`: the same outcome reported straight
// after the office recorded the approval that triggered it.
function linkedNotice(how: "own_remedial" | "attached" | "created", afterApproval: boolean): string {
  const lead = afterApproval ? "Approval recorded. " : "";
  if (how === "created") return `${lead}The repair job has been raised at the agreed price. It is on Scheduling, not yet booked or assigned.`;
  if (how === "own_remedial") return `${lead}The estimate has been linked to the repair job already raised for this defect, at the agreed price.`;
  return `${lead}The estimate has been attached to the open repair job, at the agreed price.`;
}

interface RowNotice {
  text: string;
  tone: "ok" | "info";
}

// What recording an approval did about the repair job (the response's jobRaise). A decline has none.
function approvalNotice(outcome: RaiseJobOutcome | undefined): RowNotice {
  if (!outcome) return { tone: "ok", text: "Approval recorded." };
  if (outcome.kind === "linked") return { tone: "ok", text: linkedNotice(outcome.how, true) };
  const why = sentenceOrNothing(outcome.message);
  // Session ASC: the defect's own repair job is already complete, so there is no job to decide on; the server's
  // sentence is the whole instruction (correct that job's invoice in Xero).
  if (outcome.kind === "needs_decision" && OWN_JOB_COMPLETE_CODES.has(outcome.code) && why) {
    return { tone: "info", text: `Approval recorded. ${why}` };
  }
  if (outcome.kind === "needs_decision") {
    return { tone: "info", text: `Approval recorded. The repair job needs your decision.${why ? ` ${why}` : ""}` };
  }
  return { tone: "info", text: `Approval recorded, but the repair job was not raised.${why ? ` ${why}` : ""}` };
}

// How the answer arrived, and any note that came with it: the customer's own reason on an online decline
// (plain text, never HTML), or the office's note on an answer it recorded. An answer recorded before
// Session ARS has no channel: only the office could record one then.
function AnswerLines({ e }: { e: RepairEstimateDto }) {
  const online = e.respondedVia === "online";
  const on = e.respondedAt ? ` on ${formatDate(e.respondedAt)}` : "";
  const word = e.status === "approved" ? "Approved" : "Declined";
  const note = e.responseNote?.trim() ? e.responseNote : null;
  return (
    <>
      <p className="text-ele-strong">{online ? `${word} online by the customer${on}.` : `${word}, recorded by the office${on}.`}</p>
      {note && online && (
        <p>
          {e.status === "declined" ? "The customer's reason: " : "The customer's note: "}
          <span className="whitespace-pre-wrap text-ele-strong">{note}</span>
        </p>
      )}
      {note && !online && (
        <p>
          Office note: <span className="whitespace-pre-wrap">{note}</span>
        </p>
      )}
    </>
  );
}

// Session ASC (A3, Director decision: "Send the office notice once per estimate; later reasons are stored and audited,
// not emailed"). The reasons the customer typed on declines AFTER the one on record, each dated, saying which one (at
// most one) the office inbox was emailed about, so the office reads here what never reached the inbox. Shown whatever
// the estimate's answer is now: clearing an answer does not remove the customer's words. Plain text, never HTML.
function FurtherDeclineReasons({ e }: { e: RepairEstimateDto }) {
  const reasons = (e.furtherDeclineReasons ?? []).filter((r) => r.reason.trim() !== "");
  if (reasons.length === 0) return null;
  return (
    <div role="note" aria-label="Further reasons from the customer">
      <p>The customer gave further reasons online after declining:</p>
      <ul className="ml-4 list-disc">
        {reasons.map((r, i) => (
          <li key={`${r.at}-${i}`}>
            {formatDate(r.at)}, {r.emailed ? "emailed to the office" : "not emailed"}: <span className="whitespace-pre-wrap text-ele-strong">{r.reason}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// A sent, unanswered estimate whose emailed document carries the customer's link: how long they can
// answer online. Past the price's stated validity the customer's page refuses an approval (it asks them to
// call for an updated price) but still takes a decline.
function OnlineAnswerLine({ e }: { e: RepairEstimateDto }) {
  if (e.status !== "sent" || !e.hasCustomerLink || !e.validUntil) return null;
  const until = formatDate(`${e.validUntil}T00:00:00.000Z`);
  if (londonDateKey(new Date()) > e.validUntil) {
    return (
      <p>
        The price stood until {until}, so the customer can no longer approve it online (they can still decline there). If they agree it another way,
        record their answer here.
      </p>
    );
  }
  // Review R2 (L3): the 30 days run from the day the Gmail draft was made, not the day a person sent it.
  return <p>Once the Gmail draft is sent, the customer can approve or decline online until {until} (counted from the day the draft was made).</p>;
}

// A tiny customer picker for the one case that needs it: raising the job for an estimate with no lift and
// no customer. Session SC2A: the customer is SEARCHED on the register (the shared typeahead), never read whole.
function CustomerChooser({ chosen, onChoose, disabled }: { chosen: CustomerOption | null; onChoose: (c: CustomerOption | null) => void; disabled: boolean }) {
  return (
    <div className="mt-2">
      <p>This estimate has no lift and no customer, so choose the customer the repair job is for.</p>
      {chosen ? (
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <span className="text-sm text-ele-strong">{chosen.name}</span>
          <button type="button" onClick={() => onChoose(null)} disabled={disabled} className="font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong">
            Change
          </button>
        </div>
      ) : (
        <div className="mt-1">
          <RecordPicker
            kind="customer"
            value=""
            label="Search customers by name"
            placeholder="Search customers by name"
            disabled={disabled}
            onChange={(id, rec) => {
              if (id && rec) onChoose(rec.raw as unknown as CustomerOption);
            }}
          />
        </div>
      )}
    </div>
  );
}

function EstimateRow({
  estimate: e,
  onChanged,
  onDeleteRequest,
  onRefused,
  onStale,
}: {
  estimate: RepairEstimateDto;
  onChanged: (next: RepairEstimateDto) => void;
  onDeleteRequest: (e: RepairEstimateDto) => void;
  // A 409 or 404 on one of this row's actions: the panel re-reads the list (and on a 404 takes the
  // row off it). The row keeps its own error message beside the refreshed state. The server's own words
  // are passed up because a 404 unmounts the row, and one of them carries something the office must act
  // on ("the draft is still in Gmail").
  onRefused: (e: RepairEstimateDto, status: number, serverMessage: string) => void;
  // The row learned it is out of date without being refused (a job was linked elsewhere): re-read it.
  onStale: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<RowNotice | null>(null);
  const [editing, setEditing] = useState(false);
  const [answering, setAnswering] = useState(false);
  const [note, setNote] = useState("");

  const answered = e.status === "approved" || e.status === "declined";
  // Session ATF: raised on site by an engineer with no price. Nothing can be drafted, previewed or answered until the
  // office sets one (the server refuses each by name); Edit is how the price is set.
  const awaitingPrice = e.awaitingPrice === true || e.amountPence == null;
  const sendable = !answered && !awaitingPrice;
  // A repair job carries this estimate's work (Session ARS). While it does, the answer cannot be cleared
  // and the estimate cannot be deleted: detaching the estimate, or cancelling the job, frees it. Both are
  // possible while the job is still open; only detaching while it waits for approval (Session ASC).
  const hasJob = e.jobId != null || e.job != null;
  const jobOpen = e.job != null && OPEN_JOB_STATUSES.has(e.job.status);
  const jobWaiting = e.job != null && e.job.status === WAITING_JOB_STATUS;
  const jobComplete = e.job != null && e.job.status === COMPLETE_JOB_STATUS;
  // Approved with no job: the office decides. The server derives it live; an older server that does not
  // send it is covered by the same two facts it is derived from.
  const needsDecision = !hasJob && (e.needsJobDecision === true || e.status === "approved");

  // ---- The decision: what raising the job would do, read live from the server ----
  const [options, setOptions] = useState<RepairEstimateJobOptions | null>(null);
  const [optionsState, setOptionsState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const optionsSeq = useRef(0);
  const [customer, setCustomer] = useState<CustomerOption | null>(null);
  const [siteText, setSiteText] = useState("");
  const onStaleRef = useRef(onStale);
  onStaleRef.current = onStale;

  const loadOptions = useCallback(async () => {
    const mine = ++optionsSeq.current;
    setOptionsState("loading");
    try {
      const o = await api.repairEstimates.jobOptions(e.id);
      if (mine !== optionsSeq.current) return;
      setOptions(o);
      setOptionsState("ready");
      // A job was linked since this row was read (a colleague, or the customer's own approval): the
      // decision is already made, so the row is re-read to show the job instead.
      if (o.linkedJob) onStaleRef.current();
    } catch {
      if (mine === optionsSeq.current) setOptionsState("error");
    }
  }, [e.id]);

  useEffect(() => {
    if (!needsDecision) {
      optionsSeq.current += 1; // any read still out is for a question that no longer stands
      setOptions(null);
      setOptionsState("idle");
      return;
    }
    void loadOptions();
    // e.updatedAt: a changed estimate (an answer recorded again) asks again.
  }, [needsDecision, e.updatedAt, loadOptions]);

  async function run<T>(key: string, fn: () => Promise<T>, onOk: (res: T) => void, fallback: string, onFail?: () => void) {
    if (busy) return;
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      onOk(await fn());
    } catch (err) {
      setError(friendlyApiError(err, fallback));
      if (isStaleRefusal(err)) onRefused(e, err.status, friendlyApiError(err, ""));
      onFail?.();
    } finally {
      setBusy(null);
    }
  }

  function send(via: RepairEstimateDocumentVariant) {
    void run(
      via,
      () => api.repairEstimates.send(e.id, via),
      (res) => {
        onChanged(res.estimate);
        setNotice({ tone: "ok", text: sendNotice(res) });
      },
      "Could not create the draft. Nothing was sent and the estimate is unchanged. Please try again.",
    );
  }

  function respond(outcome: "approved" | "declined") {
    const trimmed = note.trim();
    void run(
      outcome,
      () => (trimmed ? api.repairEstimates.recordResponse(e.id, outcome, trimmed) : api.repairEstimates.recordResponse(e.id, outcome)),
      (res) => {
        // The job outcome rides beside the estimate; the row keeps the estimate alone.
        const { jobRaise, ...estimate } = res;
        onChanged(estimate);
        setAnswering(false);
        setNote("");
        if (outcome === "approved") {
          setNotice(approvalNotice(jobRaise));
          // A job was linked but the estimate returned does not show it yet: read the row again.
          if (jobRaise?.kind === "linked" && estimate.jobId == null) onStale();
        }
      },
      "Could not record the answer. Please try again.",
    );
  }

  function clearAnswer() {
    void run("clear", () => api.repairEstimates.clearResponse(e.id), onChanged, "Could not clear the recorded answer. Please try again.");
  }

  function raiseJob(key: "attach" | "raise" | "new_work", body: RepairEstimateRaiseJobInput) {
    void run(
      key,
      () => api.repairEstimates.raiseJob(e.id, body),
      (res) => {
        onChanged(res.estimate);
        if (res.outcome.kind === "linked") {
          setNotice({ tone: "ok", text: linkedNotice(res.outcome.how, false) });
        } else {
          // Not a shape the route answers 200 with, but never claim a link that was not made.
          setNotice({ tone: "info", text: sentenceOrNothing(res.outcome.message) || "The repair job was not raised." });
          void loadOptions();
        }
      },
      key === "attach" ? "Could not attach the estimate to that job. Nothing was changed. Please try again." : "Could not raise the repair job. Nothing was changed. Please try again.",
      // Whatever went wrong, the question is asked again so the choices on screen are the server's current ones.
      () => void loadOptions(),
    );
  }

  function attach(job: RepairEstimateJobSummary) {
    raiseJob("attach", { action: "attach", jobId: job.id });
  }

  // Take the estimate off its job while it is still open or waits for approval (a mistaken attach), without
  // cancelling a job that may carry other work. The server clears the job's agreed price in the same write;
  // the row then asks the decision again, because the estimate is approved with no job.
  function detach() {
    void run(
      "detach",
      () => api.repairEstimates.detachJob(e.id),
      (res) => {
        onChanged(res.estimate);
        setNotice({ tone: "ok", text: "The estimate has been detached from the repair job, which no longer carries its agreed price." });
      },
      "Could not detach the estimate from the job. Nothing was changed. Please try again.",
    );
  }

  // `newWork` (Session ASC, Director decision 25 September 2026): the office confirms this estimate prices work the
  // defect's own repair job, already submitted or complete, did not do. Offered only where the server says so
  // (canRaiseNewWork); the earlier job is billed as it stands.
  function raise(o: RepairEstimateJobOptions, newWork = false) {
    const site = siteText.trim();
    if (o.needsCustomer && !customer) return setError("Choose the customer the repair job is for first.");
    if (o.needsSiteLabel && !site) return setError("Say where the work is first.");
    raiseJob(newWork ? "new_work" : "raise", {
      action: "raise",
      ...(o.needsCustomer && customer ? { customerId: customer.id } : {}),
      ...(o.needsSiteLabel ? { siteLabel: site } : {}),
      ...(newWork ? { newWork: true as const } : {}),
    });
  }

  const meta: string[] = [];
  if (e.eleNumber) meta.push(e.eleNumber);
  if (e.siteLabel) meta.push(e.siteLabel);
  if (e.customerName) meta.push(e.customerName);

  const recipient = e.recipientName && e.recipientEmail ? `${e.recipientName} (${e.recipientEmail})` : (e.recipientName ?? e.recipientEmail);

  // The decision block's own content, once the server has said what is possible.
  function decisionBody(o: RepairEstimateJobOptions) {
    if (o.linkedJob) return <p className="mt-1">A repair job has just been linked to this estimate. Refreshing...</p>;
    const open = o.openRepair;
    const where = open ? jobWhere(open) : null;
    const missingCustomer = o.canRaise && o.needsCustomer && !customer;
    const missingSite = o.canRaise && o.needsSiteLabel && !siteText.trim();
    // ATTACHING FIXED-PRICES THE WHOLE JOB. The job's invoice becomes one line at this estimate's price, parts
    // included, for everything on it, so other work on the job is not billed separately. The server sets
    // attachWarning whenever the target is not exclusively this defect's own job; when it does, the warning
    // sits beside the control in full and the confirm states the consequence, never a softer summary.
    const attachWarning = o.canAttach && open ? sentenceOrNothing(o.attachWarning) : "";
    // Session ASC (D95 (g)): the job on offer can be the defect's own repair job WAITING FOR APPROVAL, the one
    // job this estimate may go on then. It is never described as open, and the attach says which job it is. Session
    // ASC review (A-F1 (a)/(c)): it is named as LINKED to the defect, which is always true (it can be the lift's one
    // open repair a later defect was linked to, raised for other work); the server's own sentence says more.
    const openWaiting = open != null && open.status === WAITING_JOB_STATUS;
    return (
      <>
        {open && (
          <p className="mt-1">
            {openWaiting
              ? `The repair job linked to this defect has been finished by the engineer and is waiting for your approval: ${jobName(open)}.`
              : open.isDefectsOwn
                ? `The repair job already raised for this defect is open: ${jobName(open)}, ${jobWhen(open)}.`
                : `There is already an open repair on ${where ?? "this lift"}: ${jobName(open)}, ${jobWhen(open)}.`}{" "}
            <Link to={schedulingHref(open)} className="text-ele-green hover:underline">
              Open it in Scheduling
            </Link>
          </p>
        )}
        {/* Why no new job is offered beside the attach: the server's sentence, or ours if it sends none. Ours never
            says the defect's work is on a waiting job (it cannot tell whether the job is exclusively this defect's),
            and ends, as every refusal for a closed own job does, on the further-work case (Session ASC review). */}
        {open && o.canAttach && !o.canRaise && !o.blocked && (
          <p className="mt-1">
            {sentenceOrNothing(o.raiseRefusal?.message) ||
              (openWaiting
                ? "A new repair job is not raised for this defect once its repair job has been submitted. If that job includes this estimate's work, attach this estimate to it before the job is approved, so the repair is invoiced once, at the agreed price. If this estimate prices further work that job did not do, confirm it as new work on the estimate, and a separate repair job is raised for it at the agreed price."
                : "A lift has one open repair job at a time, so a new one cannot be raised here while that one is open.")}
          </p>
        )}
        {o.blocked && sentenceOrNothing(o.blocked.message) && <p className="mt-1 text-ele-strong">{o.blocked.message}</p>}
        {o.canRaise && (
          <>
            <p className="mt-1">
              Raising makes a new repair job at the agreed price of {priceWords(e)}, not yet booked or assigned. It waits on Scheduling for you to book it.
            </p>
            {o.needsCustomer && <CustomerChooser chosen={customer} onChoose={setCustomer} disabled={busy != null} />}
            {o.needsSiteLabel && (
              <label className="mt-2 block">
                <span className="mb-1 block">This estimate does not say where the work is. Where is it (the site or address)?</span>
                <input maxLength={MAX_SITE_LABEL} value={siteText} onChange={(ev) => setSiteText(ev.target.value)} className={INPUT} />
              </label>
            )}
          </>
        )}
        {!o.canAttach && !o.canRaise && !o.blocked && <p className="mt-1">Neither attaching nor raising a job is possible right now.</p>}
        {attachWarning && (
          <p role="note" aria-label="Before you attach" className="mt-2 rounded-ele border border-ele-danger px-3 py-2 text-sm font-semibold text-ele-strong">
            {attachWarning}
          </p>
        )}
        {(o.canAttach || o.canRaise) && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {o.canAttach && open && (
              <ConfirmButton
                onConfirm={() => attach(open)}
                disabled={busy != null || optionsState === "loading"}
                busy={busy === "attach"}
                busyLabel="Attaching..."
                label={
                  openWaiting
                    ? "Attach to the repair job waiting for approval"
                    : where
                      ? `Attach to the open repair on ${where}`
                      : "Attach to the open repair job"
                }
                confirmLabel={
                  attachWarning ? `Confirm: attach, and bill this whole job at ${priceWords(e)} ex VAT` : `Confirm: attach to ${jobName(open, 60)}`
                }
                className={buttonClasses("secondary", "sm")}
              />
            )}
            {o.canRaise && (
              <ConfirmButton
                onConfirm={() => raise(o)}
                disabled={busy != null || optionsState === "loading" || missingCustomer || missingSite}
                busy={busy === "raise"}
                busyLabel="Raising..."
                label="Raise the repair job"
                // An estimate approved before online answers may already have a repair raised by hand, possibly
                // one that is FINISHED, which nothing here can see: the confirm itself repeats the caution.
                confirmLabel={e.approvedBeforeOnlineAnswers ? "Confirm: raise a NEW repair job (check Scheduling first)" : "Confirm: raise the repair job"}
                className={`${SMALL_BTN} border-ele-green text-ele-green`}
              />
            )}
          </div>
        )}
        {(missingCustomer || missingSite) && (
          <p className="mt-1 opacity-70">{missingCustomer ? "Choose the customer above to raise the job." : "Say where the work is above to raise the job."}</p>
        )}
        {o.canRaiseNewWork === true && newWorkBlock(o)}
      </>
    );
  }

  // Session ASC (Director decision 25 September 2026): the defect's own repair job has been submitted or completed, and
  // the rule above stays the default. Only if this estimate prices work that job did NOT do may the office raise a
  // separate repair job at the agreed price, by confirming exactly that. Deliberately secondary to the refusal: smaller,
  // quieter, and below it, with the server's own sentence naming the earlier job and its invoice.
  function newWorkBlock(o: RepairEstimateJobOptions) {
    const needsChooser = !o.canRaise;
    const missing = (o.needsCustomer && !customer) || (o.needsSiteLabel && !siteText.trim());
    return (
      <div role="group" aria-label="Only if this is new work" className="mt-3 border-t border-ele-hairline pt-2 text-xs">
        <p className="opacity-80">
          {sentenceOrNothing(o.newWorkConfirm) ||
            "Confirming this is new work raises a separate repair job at this estimate's agreed price, for work the earlier repair job did not do, and that job is billed as it stands."}
        </p>
        {needsChooser && o.needsCustomer && <CustomerChooser chosen={customer} onChoose={setCustomer} disabled={busy != null} />}
        {needsChooser && o.needsSiteLabel && (
          <label className="mt-2 block">
            <span className="mb-1 block">This estimate does not say where the work is. Where is it (the site or address)?</span>
            <input maxLength={MAX_SITE_LABEL} value={siteText} onChange={(ev) => setSiteText(ev.target.value)} className={INPUT} />
          </label>
        )}
        <div className="mt-2">
          <ConfirmButton
            onConfirm={() => raise(o, true)}
            disabled={busy != null || optionsState === "loading" || missing}
            busy={busy === "new_work"}
            busyLabel="Raising..."
            label="This is new work: raise a separate job"
            confirmLabel={`Confirm: new work the earlier job did not do, raise a separate job at ${priceWords(e)} ex VAT`}
            className={`${SMALL_BTN} border-ele-purple-accent text-ele-body`}
          />
        </div>
      </div>
    );
  }

  return (
    <li className="border-t border-ele-hairline py-3 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-menu text-sm tracking-wide text-ele-loler">{e.reference}</span>
        <StatusPill domain="repairEstimate" status={e.status} />
        {e.raisedOnSite && <StatusPill word="Raised on site" tone="blue" />}
        {awaitingPrice ? (
          <StatusPill word="Awaiting price" tone="amber" />
        ) : (
          <span className="text-sm text-ele-strong">
            {priceWords(e)} <span className="text-xs text-ele-body">ex VAT</span>
          </span>
        )}
      </div>
      <p className="mt-1 whitespace-pre-wrap text-sm text-ele-body">{e.description}</p>
      <div className="mt-1 space-y-0.5 text-xs text-ele-body">
        {meta.length > 0 && (
          <div className="flex items-center">
            {/* Session SCR1 (SC9B follow-up): the account symbol beside the customer's name on the estimate row. It sits OUTSIDE the faded
                line: opacity would wash the solid fill whose contrast the lint measures. */}
            {e.customerId && <AccountSymbol customerId={e.customerId} className="!ml-0" />}
            <p className="opacity-70">{meta.join(" · ")}</p>
          </div>
        )}
        <p>{recipient ? `For ${recipient}` : "No recipient recorded"}</p>
        {e.sentVia && e.sentAt && (
          <p>
            Last drafted: {SEND_PATH_LABEL[e.sentVia]}, {formatDate(e.sentAt)}.
          </p>
        )}
        <OnlineAnswerLine e={e} />
        {e.xeroInvoiceId && (
          <p>
            <a
              href={`https://go.xero.com/AccountsReceivable/Edit.aspx?InvoiceID=${encodeURIComponent(e.xeroInvoiceId)}`}
              target="_blank"
              rel="noreferrer"
              className="text-ele-loler underline"
            >
              {e.xeroInvoiceNumber ? `Xero draft invoice ${e.xeroInvoiceNumber} (View in Xero)` : "Xero draft invoice (View in Xero)"}
            </a>
          </p>
        )}
        {answered && <AnswerLines e={e} />}
        <FurtherDeclineReasons e={e} />
      </div>

      {e.raisedOnSite && (
        // Session ATF: where this estimate came from, and what the engineer recorded for pricing. The work is in their
        // words, so it wants a first look before the customer reads it; the parts and the hours never reach the customer.
        <div role="note" aria-label="Raised on site" className={NOTE_BOX}>
          <p className="text-ele-strong">
            Raised on site{e.raisedOnSiteByName ? ` by ${e.raisedOnSiteByName}` : ""}, {formatDate(e.createdAt)}
            {e.raisedFromJob ? (
              <>
                , from the job {jobName(e.raisedFromJob)}.{" "}
                <Link to={schedulingHref(e.raisedFromJob)} className="text-ele-green hover:underline">
                  Open it in Scheduling
                </Link>
              </>
            ) : (
              "."
            )}
          </p>
          {e.partsNeeded?.trim() && (
            <p className="mt-1">
              Parts the engineer says it needs: <span className="whitespace-pre-wrap text-ele-strong">{e.partsNeeded}</span>
            </p>
          )}
          {e.estimatedHours != null && <p className="mt-1">The engineer's rough time on site: {hoursWords(e.estimatedHours)}.</p>}
          {awaitingPrice && (
            <p className="mt-1 font-semibold text-ele-strong">
              Engineers do not price work. Check the wording the customer will read, then set the price with Edit before drafting it to the
              customer.
            </p>
          )}
        </div>
      )}

      {hasJob && (
        // "Repair job", not "raised": the estimate may have been attached to a job that already existed.
        // What the office is told to do follows the job's state: an open job can be detached from or
        // cancelled, one waiting for approval can only be detached from (Session ASC), and a complete job
        // carries no instruction at all.
        <div role="note" aria-label="Repair job" className={NOTE_BOX}>
          {e.job ? (
            <p className="text-ele-strong">
              Repair job: {jobName(e.job)}, {jobWhen(e.job)}.
            </p>
          ) : (
            <p className="text-ele-strong">A repair job is linked to this estimate.</p>
          )}
          <p className="mt-1">
            {jobComplete
              ? "It carries this estimate's agreed price, so this approval is the record of that price."
              : "The job carries this estimate's agreed price."}{" "}
            <Link to={schedulingHref(e.job ?? { id: e.jobId ?? "", scheduledFor: null })} className="text-ele-green hover:underline">
              Open it in Scheduling
            </Link>
          </p>
          {(jobOpen || jobWaiting) && e.job && (
            <>
              <p className="mt-1 opacity-70">
                {jobWaiting
                  ? "To clear the answer or delete this estimate, detach it from the job first. If the job is approved without this estimate on it, its draft invoice bills the hours and parts recorded, not the agreed price."
                  : "To clear the answer or delete this estimate, detach it or cancel the repair job first."}
              </p>
              <div className="mt-2">
                <ConfirmButton
                  onConfirm={detach}
                  disabled={busy != null}
                  busy={busy === "detach"}
                  busyLabel="Detaching..."
                  label="Detach from this job"
                  confirmLabel={`Confirm: detach from ${jobName(e.job, 60)}`}
                  className={`${SMALL_BTN} border-ele-purple-accent text-ele-body`}
                />
              </div>
            </>
          )}
        </div>
      )}

      {needsDecision && (
        <div role="group" aria-label="Repair job decision" className={NOTE_BOX}>
          {/* Approved under ARL, before online answers existed: the panel then told the office to raise the
              repair by hand from Scheduling, so one may already exist that nothing links here. The options only
              ever look at OPEN jobs, so a hand-raised repair that is already FINISHED is invisible to them and
              "Raise" is offered; pressing it would make a second job, and a second invoice when that completes.
              So the caution is said FIRST, names finished jobs, and is repeated in the raise confirm itself. The
              choices stay: the office may find nothing and still need to raise. */}
          {e.approvedBeforeOnlineAnswers && (
            <p role="note" aria-label="Approved before online answers" className="mb-2 font-semibold text-ele-strong">
              This estimate was approved before customers could answer online, when repair jobs were raised by hand. Check{" "}
              <Link to="/office/scheduling" className="text-ele-green hover:underline">
                Scheduling
              </Link>{" "}
              for a repair already raised for it, including finished jobs, before you attach or raise one here.
            </p>
          )}
          {/* Session ASC: once the defect's own repair job is complete there is nothing to choose here; the server's
              sentence below names that job's Xero draft to correct. */}
          <p className="text-ele-strong">
            {options?.blocked && OWN_JOB_COMPLETE_CODES.has(options.blocked.code) && options.canRaiseNewWork !== true
              ? "No repair job carries this work, and none can now."
              : "No repair job carries this work yet. Choose what happens to it."}
          </p>
          {optionsState === "loading" && !options && <p className="mt-1">Checking the open jobs...</p>}
          {optionsState === "error" && (
            <p role="alert" className="mt-1 text-ele-danger">
              Could not check which repair job this belongs to.{" "}
              <button type="button" onClick={() => void loadOptions()} className="underline">
                Retry
              </button>
            </p>
          )}
          {options && decisionBody(options)}
        </div>
      )}

      {editing ? (
        <EstimateForm
          title={awaitingPrice ? `Set the price for ${e.reference}` : `Edit ${e.reference}`}
          initial={{
            description: e.description,
            price: e.amountPence == null ? "" : (e.amountPence / 100).toFixed(2),
            recipientName: e.recipientName ?? "",
            recipientEmail: e.recipientEmail ?? "",
          }}
          submitLabel="Save changes"
          busyLabel="Saving..."
          recipientHint="Clear a field to remove it."
          onSubmit={async (v) => {
            try {
              const dto = await api.repairEstimates.update(e.id, {
                description: v.description,
                amountPence: v.amountPence,
                recipientName: v.recipientName || null,
                recipientEmail: v.recipientEmail || null,
              });
              onChanged(dto);
              setEditing(false);
            } catch (err) {
              // Anything else (a bad value) is the form's to show, with the form left open to fix it.
              if (!isStaleRefusal(err)) throw err;
              // The estimate moved on under the form (it was sent or it was deleted), so no edit can land: close the form, say why, and re-read the row.
              setEditing(false);
              setError(friendlyApiError(err, "Could not save the changes. Please try again."));
              onRefused(e, err.status, friendlyApiError(err, ""));
            }
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          {sendable && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {e.recipientEmail && (
                <>
                  <ConfirmButton
                    onConfirm={() => send("priced_document")}
                    disabled={busy != null}
                    busy={busy === "priced_document"}
                    busyLabel="Drafting..."
                    label="Draft the priced letter"
                    confirmLabel={`Confirm: letter to ${e.recipientEmail}`}
                    armedNote={<AccountOnStopNote customerId={e.customerId} />}
                    className={`${SMALL_BTN} border-ele-loler text-ele-loler`}
                  />
                  <ConfirmButton
                    onConfirm={() => send("quotation")}
                    disabled={busy != null}
                    busy={busy === "quotation"}
                    busyLabel="Drafting..."
                    label="Draft the repair quotation"
                    confirmLabel={`Confirm: quotation to ${e.recipientEmail}`}
                    armedNote={<AccountOnStopNote customerId={e.customerId} />}
                    className={`${SMALL_BTN} border-ele-loler text-ele-loler`}
                  />
                </>
              )}
            </div>
          )}
          {sendable && !e.recipientEmail && (
            <p className="mt-1 text-xs text-ele-body opacity-70">
              {e.status === "draft"
                ? "No email address on record, so the letter and the quotation cannot be drafted yet. Use Edit to add one."
                : "No email address on record, so the letter and the quotation cannot be drafted."}
            </p>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-2">
            {!awaitingPrice && (
              <>
                <a href={api.repairEstimates.documentUrl(e.id, "priced_document")} target="_blank" rel="noreferrer" className={`${SMALL_BTN} border-ele-purple-accent text-ele-body`}>
                  Preview letter
                </a>
                <a href={api.repairEstimates.documentUrl(e.id, "quotation")} target="_blank" rel="noreferrer" className={`${SMALL_BTN} border-ele-purple-accent text-ele-body`}>
                  Preview quotation
                </a>
              </>
            )}
            {e.status === "draft" && (
              awaitingPrice ? (
                <Button variant="secondary" size="sm" onClick={() => setEditing(true)} disabled={busy != null}>
                  Set the price
                </Button>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  disabled={busy != null}
                  className={`${SMALL_BTN} border-ele-purple-accent text-ele-body`}
                >
                  Edit
                </button>
              )
            )}
            {sendable && !answering && (
              <Button variant="secondary" size="sm" onClick={() => setAnswering(true)} disabled={busy != null}>
                Record the customer's answer
              </Button>
            )}
            {answered && !hasJob && (
              <ConfirmButton
                onConfirm={clearAnswer}
                disabled={busy != null}
                busy={busy === "clear"}
                busyLabel="Clearing..."
                label="Clear the recorded answer"
                confirmLabel="Confirm: clear the answer"
                className={`${SMALL_BTN} border-ele-purple-accent text-ele-body`}
              />
            )}
            {e.status !== "approved" && !hasJob && (
              <button type="button" onClick={() => onDeleteRequest(e)} disabled={busy != null} className={`${SMALL_BTN} border-ele-danger text-ele-danger`}>
                Delete
              </button>
            )}
          </div>

          {answering && sendable && (
            <div className="mt-2 rounded-ele border border-ele-purple-accent bg-ele-bg p-3">
              <label className="block text-sm">
                <span className="mb-1 block text-ele-body">How the answer arrived (optional)</span>
                <input maxLength={1000} value={note} onChange={(ev) => setNote(ev.target.value)} placeholder="For example: approved by phone" className={INPUT} />
              </label>
              <p className="mt-1 text-xs text-ele-body opacity-70">
                Recording an approval raises the repair job at this price when it is clear which job the work belongs to. When it is not clear, you choose here.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {/* Session ARS: two-step, the branded-confirm rule for a consequential action. An approval can
                    now raise the repair job at this price, so the confirm states the price being agreed. */}
                <ConfirmButton
                  onConfirm={() => respond("approved")}
                  disabled={busy != null}
                  busy={busy === "approved"}
                  busyLabel="Saving..."
                  label="Record approved"
                  confirmLabel={`Confirm: approved at ${priceWords(e)} ex VAT`}
                  className={buttonClasses("secondary", "sm")}
                />
                <ConfirmButton
                  onConfirm={() => respond("declined")}
                  disabled={busy != null}
                  busy={busy === "declined"}
                  busyLabel="Saving..."
                  label="Record declined"
                  confirmLabel="Confirm: declined"
                  className={`${SMALL_BTN} border-ele-danger text-ele-danger`}
                />
                <button
                  type="button"
                  onClick={() => {
                    setAnswering(false);
                    setNote("");
                  }}
                  disabled={busy != null}
                  className="font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {notice && (
        <div role="status" className="mt-2 text-xs">
          <p className={notice.tone === "ok" ? "text-ele-green" : "text-ele-strong"}>{notice.text}</p>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-2 text-xs text-ele-danger">
          {error}
        </p>
      )}
    </li>
  );
}

// Where the panel is mounted, in the office's words: the empty list and the form's recipient hint.
function originCopy(defectId?: string, calloutId?: string): { empty: string; recipientHint: string } {
  if (defectId) return { empty: "No repair estimate has been raised from this defect yet.", recipientHint: "Leave these blank to use the customer's details from the defect." };
  if (calloutId) return { empty: "No repair estimate has been raised from this call-out yet.", recipientHint: "Leave these blank to use the customer's details from the call-out." };
  return { empty: "No repair estimate has been raised for this customer yet.", recipientHint: "Leave these blank to use this customer's billing contact." };
}

interface PanelNotice {
  text: string;
  tone: "ok" | "info";
}

// Session SC7A: the Repairs hub's Repair estimates tab lists EVERY estimate, 50 at a time, filtered by status. It is this same panel
// with the same rows and the same actions (send, record the answer, raise the job, delete), switched into "every estimate" mode.
const ESTIMATE_PAGE_SIZE = 50;

// A status filter chip: quiet, with a green outline only on the one that is on.
function statusChipClass(isActive: boolean): string {
  const active = "border-ele-green bg-ele-inset text-ele-green";
  return `rounded-ele border px-3 py-1.5 font-menu text-xs tracking-wide transition max-sm:min-h-11 ${isActive ? active : "border-ele-purple-accent text-ele-body hover:text-ele-green"}`;
}
type EstimateStatusFilter = "all" | "draft" | "awaiting" | "approved" | "declined";
const ESTIMATE_STATUS_FILTERS: { value: EstimateStatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "awaiting", label: "Awaiting an answer" },
  { value: "approved", label: "Approved" },
  { value: "declined", label: "Declined" },
];

export function RepairEstimatesPanel({
  defectId,
  calloutId,
  customerId,
  listAll = false,
  defaultDescription,
  className = "rounded-ele border border-ele-purple-accent bg-ele-surface p-5",
}: {
  // Every estimate, paged and filterable (the Repairs hub tab). Mutually exclusive with the three links below.
  listAll?: boolean;
  defectId?: string;
  calloutId?: string;
  // The customer page: every estimate for this customer, whatever it was raised from, and "Quote
  // this" prices a repair for the customer with no defect or call-out behind it.
  customerId?: string;
  // The starting wording for "Quote this" (the defect page passes what was found).
  defaultDescription?: string | null;
  className?: string;
}) {
  const [estimates, setEstimates] = useState<RepairEstimateDto[]>([]);
  // Every-estimate mode (listAll): the status chip, the page the person is on and the server's true total.
  const [statusFilter, setStatusFilter] = useState<EstimateStatusFilter>("all");
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [creating, setCreating] = useState(false);
  const [notice, setNotice] = useState<PanelNotice | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RepairEstimateDto | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Two counters decide whether a list answer may land. `loadSeq` numbers every read, so only the
  // latest one is applied; `changeSeq` counts every change made HERE (a create, an edit, a send, an
  // answer, a delete), so a read that was already out when one landed never overwrites it with the
  // older list. Without them a slow first read answered after a create put the pre-create list back
  // and the new draft vanished until the page was reloaded.
  const loadSeq = useRef(0);
  const changeSeq = useRef(0);

  // `silent`: re-read without replacing the list with the loading placeholder (after a create or a refusal the
  // rows stay on screen while the answer comes back). `fresh`: the cached copy is known to be stale.
  const load = useCallback(
    async (opts: { fresh?: boolean; silent?: boolean } = {}): Promise<void> => {
      const mine = ++loadSeq.current;
      const changesAtStart = changeSeq.current;
      if (!opts.silent) setLoadState("loading");
      const params = { ...(defectId ? { defectId } : {}), ...(calloutId ? { calloutId } : {}), ...(customerId ? { customerId } : {}) };
      try {
        const list = listAll
          ? await api.repairEstimates
              .listPage({ ...(statusFilter !== "all" ? { status: statusFilter } : {}), limit: ESTIMATE_PAGE_SIZE, offset }, { fresh: opts.fresh })
              .then((page) => {
                setTotal(page.total);
                return page.items;
              })
          : await (opts.fresh ? api.repairEstimates.list(params, { fresh: true }) : api.repairEstimates.list(params));
        if (mine !== loadSeq.current) return; // a later read is on its way and will land instead
        if (changesAtStart !== changeSeq.current) {
          // Something changed here while this read was out, so its answer is older than what is on
          // screen. Ask again rather than put the older list back.
          void load({ fresh: true, silent: true });
          return;
        }
        setEstimates(list);
        setLoadState("ready");
      } catch {
        if (mine !== loadSeq.current) return;
        // A background re-read that fails leaves a list that loaded fine alone; anything else says so,
        // with Retry, while any row already known stays on screen below the message.
        setLoadState((s) => (opts.silent && s === "ready" ? s : "error"));
      }
    },
    [defectId, calloutId, customerId, listAll, statusFilter, offset],
  );

  useEffect(() => {
    // A new filter (the call-out hub keeps this panel mounted as the office moves between call-outs)
    // starts from nothing, so one record's estimates are never shown or acted on under another's.
    setEstimates([]);
    setCreating(false);
    setNotice(null);
    setDeleteTarget(null);
    setDeleteError(null);
    void load();
  }, [load]);

  const replace = (next: RepairEstimateDto) => {
    changeSeq.current += 1;
    setEstimates((list) => list.map((x) => (x.id === next.id ? next : x)));
  };

  const drop = (id: string) => {
    changeSeq.current += 1;
    setEstimates((list) => list.filter((x) => x.id !== id));
  };

  // A row's action was refused because the row is out of date. A 404 means the estimate has gone
  // (deleted elsewhere): take it off the list and close the delete modal, which can only be about a
  // row that no longer exists. Either way re-read, so every row shows its real state.
  const refused = (target: RepairEstimateDto, status: number, serverMessage = "") => {
    if (status === 404) {
      drop(target.id);
      setDeleteTarget(null);
      setDeleteError(null);
      // The row that would have shown the server's message is gone, so the message is shown here, in the
      // server's own words, ahead of what happened to the list.
      const taken = `Estimate ${target.reference} no longer exists (it may have been deleted elsewhere), so it has been taken off this list.`;
      setNotice({ tone: "info", text: serverMessage ? `${serverMessage} ${taken}` : taken });
    }
    void load({ fresh: true, silent: true });
  };

  async function confirmDelete() {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const res = await api.repairEstimates.remove(target.id);
      drop(target.id);
      setDeleteTarget(null);
      setNotice({ tone: "ok", text: [`Estimate ${target.reference} deleted.`, ...(res.notes ?? [])].join(" ") });
    } catch (err) {
      if (isStaleRefusal(err) && err.status === 404) {
        refused(target, 404, friendlyApiError(err, ""));
      } else {
        // A 409 (approved, or a repair job linked meanwhile) stays in the modal in the server's words,
        // and the row behind it is re-read so it stops offering a delete the server will refuse.
        setDeleteError(friendlyApiError(err, "Could not delete this estimate. Please try again."));
        if (isStaleRefusal(err)) void load({ fresh: true, silent: true });
      }
    } finally {
      setDeleteBusy(false);
    }
  }

  const origin = originCopy(defectId, calloutId);

  return (
    <section aria-label="Repair estimates" className={className}>
      {/* In every-estimate mode the page supplies the heading and the note (RepairEstimatesListPage). */}
      {!listAll && (
        <>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="font-heading text-lg tracking-wide text-ele-strong">Repair estimates</h2>
          {!creating && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setNotice(null);
                setCreating(true);
              }}
            >
              Quote this
            </Button>
          )}
        </div>
        <p className="mt-1 text-xs text-ele-body opacity-70">
          Price a repair before the customer agrees it. Nothing is sent from here: each send makes a draft in Gmail for you to review and send.
        </p>
        </>
      )}
      {listAll && (
        <div role="group" aria-label="Estimate status" className="mt-3 flex flex-wrap gap-2">
          {ESTIMATE_STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              aria-pressed={statusFilter === f.value}
              onClick={() => {
                setStatusFilter(f.value);
                setOffset(0);
              }}
              className={statusChipClass(statusFilter === f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {creating && (
        <EstimateForm
          title="Quote this repair"
          initial={{ description: defaultDescription ?? "", price: "", recipientName: "", recipientEmail: "" }}
          submitLabel="Save as a draft"
          busyLabel="Saving..."
          recipientHint={origin.recipientHint}
          onSubmit={async (v) => {
            const dto = await api.repairEstimates.create({
              ...(defectId ? { defectId } : {}),
              ...(calloutId ? { calloutId } : {}),
              ...(customerId ? { customerId } : {}),
              description: v.description,
              amountPence: v.amountPence,
              ...(v.recipientName ? { recipientName: v.recipientName } : {}),
              ...(v.recipientEmail ? { recipientEmail: v.recipientEmail } : {}),
            });
            // Shown at once, whatever state the list is in (still loading, or failed to load), and
            // then the list is read again so it is complete and the new row is the server's own.
            changeSeq.current += 1;
            setEstimates((list) => [dto, ...list.filter((x) => x.id !== dto.id)]);
            setCreating(false);
            setNotice({ tone: "ok", text: `Estimate ${dto.reference} saved as a draft. Choose how to put it in front of the customer below.` });
            void load({ fresh: true, silent: true });
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {notice && (
        <p role="status" className={`mt-3 text-sm ${notice.tone === "ok" ? "text-ele-green" : "text-ele-body"}`}>
          {notice.text}
        </p>
      )}

      <div className="mt-3 space-y-3">
        {loadState === "error" && (
          <p role="alert" className="text-sm text-ele-danger">
            Could not load the repair estimates.{" "}
            <button type="button" onClick={() => void load()} className="underline">
              Retry
            </button>
          </p>
        )}
        {loadState === "loading" && estimates.length === 0 && <Loader shape="inline" />}
        {loadState === "ready" && estimates.length === 0 && (
          <p role={listAll && statusFilter !== "all" ? "status" : undefined} className="text-sm text-ele-body opacity-70">
            {listAll ? (statusFilter !== "all" ? "No estimates match this filter." : "No repair estimate has been raised yet.") : origin.empty}
          </p>
        )}
        {estimates.length > 0 && (
          <ul>
            {estimates.map((e) => (
              <EstimateRow
                key={e.id}
                estimate={e}
                onChanged={replace}
                onDeleteRequest={(target) => {
                  setDeleteError(null);
                  setDeleteTarget(target);
                }}
                onRefused={refused}
                onStale={() => void load({ fresh: true, silent: true })}
              />
            ))}
          </ul>
        )}
      </div>

      {listAll && loadState === "ready" && <Pager offset={offset} total={total} shown={estimates.length} pageSize={ESTIMATE_PAGE_SIZE} onOffset={setOffset} noun={["estimate", "estimates"]} />}

      <ConfirmDeleteModal
        open={deleteTarget != null}
        title={deleteTarget ? `Delete estimate ${deleteTarget.reference}?` : "Delete estimate?"}
        message={deleteTarget ? deleteMessage(deleteTarget) : "This permanently deletes the estimate. This cannot be undone."}
        confirmLabel="Delete estimate"
        busy={deleteBusy}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={() => {
          if (deleteBusy) return;
          setDeleteTarget(null);
          setDeleteError(null);
        }}
      />
    </section>
  );
}
